import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InternalTaskBoard } from './InternalTaskBoard';
import { ProspectPipeline } from './ProspectPipeline';
import { Target, CheckCircle, Users, TrendingUp } from 'lucide-react';

export function SalesAgentDashboard() {
  const { user } = useAuth();

  const { data: myTeam } = useQuery({
    queryKey: ['my-sales-team', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase.from('internal_team_members').select('team_id').eq('user_id', user.id).limit(1).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: stats } = useQuery({
    queryKey: ['sales-agent-stats', user?.id],
    queryFn: async () => {
      if (!user) return { totalProspects: 0, converted: 0, activeTasks: 0, completedTasks: 0 };
      const [prospects, tasks] = await Promise.all([
        supabase.from('sales_prospects').select('status', { count: 'exact' }).eq('sales_agent_id', user.id),
        supabase.from('internal_tasks').select('status', { count: 'exact' }).eq('assigned_to', user.id),
      ]);
      const prospectData = prospects.data || [];
      const taskData = tasks.data || [];
      return {
        totalProspects: prospectData.length,
        converted: prospectData.filter((p: any) => p.status === 'converted').length,
        activeTasks: taskData.filter((t: any) => t.status !== 'done' && t.status !== 'cancelled').length,
        completedTasks: taskData.filter((t: any) => t.status === 'done').length,
      };
    },
    enabled: !!user,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Sales Dashboard</h2>
        <p className="text-muted-foreground">Manage your prospects and tasks</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><Target className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{stats?.totalProspects || 0}</p><p className="text-xs text-muted-foreground">Total Prospects</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle className="h-8 w-8 text-green-500" /><div><p className="text-2xl font-bold">{stats?.converted || 0}</p><p className="text-xs text-muted-foreground">Converted</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><TrendingUp className="h-8 w-8 text-orange-500" /><div><p className="text-2xl font-bold">{stats?.activeTasks || 0}</p><p className="text-xs text-muted-foreground">Active Tasks</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle className="h-8 w-8 text-primary" /><div><p className="text-2xl font-bold">{stats?.completedTasks || 0}</p><p className="text-xs text-muted-foreground">Tasks Done</p></div></CardContent></Card>
      </div>

      <InternalTaskBoard teamId={myTeam?.team_id} assignedToMe />
      <ProspectPipeline />
    </div>
  );
}
